import { store } from '../store/store';
import { logout } from '../store/slices/authSlice';

/**
 * Utility functions for API requests
 */

// Function to show session expired notification
// This will be set by the app on initialization
let showSessionExpiredNotification = null;

// Function to register the notification handler
export const registerSessionExpiredHandler = (handler) => {
  showSessionExpiredNotification = handler;
};

/**
 * Get the authentication token from localStorage
 * @returns {string|null} The token or null if not found
 */
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Standard headers for API requests
 * @param {boolean} includeContentType Whether to include Content-Type header
 * @returns {Object} Headers object
 */
export const getApiHeaders = (includeContentType = false) => {
  const token = getAuthToken();
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  };
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
};

/**
 * Utility function to debug API responses
 * @param {Response} response - The fetch API response object
 * @returns {Promise<Object>} - Debug information about the response
 */
export const debugApiResponse = async (response) => {
  try {
    // Clone the response to avoid consuming it
    const clonedResponse = response.clone();
    
    // Get basic response info
    const debug = {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      headers: {},
      contentType: response.headers.get('content-type'),
      contentLength: response.headers.get('content-length'),
      url: response.url,
      redirected: response.redirected,
      type: response.type,
    };
    
    // Get all headers
    response.headers.forEach((value, key) => {
      debug.headers[key] = value;
    });
    
    // Try to get the response body as text
    try {
      const text = await clonedResponse.text();
      debug.bodyLength = text.length;
      debug.bodyPreview = text.substring(0, 500) + (text.length > 500 ? '...' : '');
      
      // Try to parse as JSON
      try {
        debug.isJson = true;
        debug.jsonParsed = JSON.parse(text);
      } catch (e) {
        debug.isJson = false;
        debug.jsonParseError = e.message;
        
        // Check if it's HTML
        if (text.trim().startsWith('<') && text.includes('</html>')) {
          debug.isHtml = true;
          debug.htmlTitle = text.match(/<title>(.*?)<\/title>/i)?.[1] || 'No title';
        }
      }
    } catch (e) {
      debug.bodyError = e.message;
    }
    
    return debug;
  } catch (error) {
    return {
      error: error.message,
      errorStack: error.stack
    };
  }
};

/**
 * Utility function to check if a token is valid
 * @returns {boolean} - Whether the token is valid
 */
export const isTokenValid = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  
  try {
    // JWT tokens are in the format header.payload.signature
    const payload = token.split('.')[1];
    if (!payload) return false;
    
    // Decode the base64 payload
    const decodedPayload = JSON.parse(atob(payload));
    
    // Check if the token has expired
    const currentTime = Math.floor(Date.now() / 1000);
    return decodedPayload.exp > currentTime;
  } catch (e) {
    console.error('Error checking token validity:', e);
    return false;
  }
};

/**
 * Utility function to get a fresh token if needed
 * @returns {Promise<string|null>} - The token or null if not available
 */
export const getFreshToken = async () => {
  const token = localStorage.getItem('token');
  
  // If no token exists or token is valid, return current token
  if (!token) return null;
  if (isTokenValid()) return token;
  
  // Token exists but is expired, try to refresh
  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.token) {
        localStorage.setItem('token', data.token);
        return data.token;
      }
    }
    
    // If refresh failed, clear the token
    localStorage.removeItem('token');
    return null;
  } catch (e) {
    console.error('Error refreshing token:', e);
    return null;
  }
};

/**
 * Make an API request with standard error handling
 * @param {string} url The URL to fetch
 * @param {Object} options Fetch options
 * @returns {Promise<Object>} The response data
 * @throws {Error} If the request fails
 */
export const apiRequest = async (url, options = {}) => {
  const response = await fetch(url, options);
  
  // Check if the response is OK
  if (!response.ok) {
    // Handle 401 Unauthorized responses (expired token)
    if (response.status === 401) {
      console.log('Token expired or invalid, logging out user');
      // Dispatch logout action to clear auth state
      store.dispatch(logout());
      
      // Show notification if handler is registered
      if (showSessionExpiredNotification) {
        showSessionExpiredNotification();
      }
      
      // Redirect to login page
      window.location.href = '/login';
      throw new Error('Your session has expired. Please log in again.');
    }
    
    // Debug the response
    const debug = await debugApiResponse(response);
    console.error('API request failed:', debug);
    
    // Try to get error message from response
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      if (response.headers.get('content-type')?.includes('application/json')) {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      }
    } catch (e) {
      // Ignore parsing errors
    }
    
    throw new Error(errorMessage);
  }
  
  // Check if the response is JSON
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error('Server returned non-JSON response');
  }
  
  return response.json();
};

export const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  
  // Ensure we're using the correct protocol
  const protocol = window.location.protocol;
  const host = window.location.host;
  
  // If the URL starts with '/api', prepend the current protocol and host
  const fullUrl = url.startsWith('/api') ? `${protocol}//${host}${url}` : url;

  const response = await fetch(fullUrl, {
    ...options,
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  
  // Handle 401 Unauthorized responses (expired token)
  if (response.status === 401) {
    console.log('Token expired or invalid, logging out user');
    // Dispatch logout action to clear auth state
    store.dispatch(logout());
    
    // Show notification if handler is registered
    if (showSessionExpiredNotification) {
      showSessionExpiredNotification();
    }
    
    // Redirect to login page
    window.location.href = '/login';
    throw new Error('Your session has expired. Please log in again.');
  }
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }
  
  return data;
}; 